<script lang="ts" setup>
import MessageBox from './components/MessageBox.vue'
import type { IForgotPasswordParams, IResetPasswordParams } from '~/api/auth'
import type { FormInstance } from 'element-plus/es/components/form'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useReCaptchaV3 } from '@/utils/helper'
import useSeo from './seo_forget'

const { t } = useI18n()
const useReCaptchaInstance = useReCaptcha()!
const route = useRoute()
const token = route.query.token as string
const email = route.query.email as string
const { auth } = useApi()
const formRef = ref<FormInstance>()
const resetFormRef = ref<FormInstance>()
const form = reactive({
  email: ''
})
const resetForm = reactive({
  password: '',
  confirm_password: ''
})
const sendMailSuccess = ref(false)
const resetPasswordSuccess = ref(false)
const submitPedding = ref(false)
const isResetPwd = ref(!!token && !!email)

const handleCheckMail = async () => {
  if (!formRef.value) return false
  // 避免double click
  if (submitPedding.value === false) {
    submitPedding.value = true
    await formRef.value.validate(async (valid, invalidFields) => {
      if (valid) {
        const googleReCaptchaToken = await useReCaptchaV3(useReCaptchaInstance, 'forgot')

        const params = {
          email: form.email,
        } as IForgotPasswordParams

        const { data, pending, error } = await auth.forgotPassword(params)
        if (error.value) {
          ElNotification({
            message: error.value.message,
          })
        } else {
          form.email = ''
          formRef.value?.resetFields()

          window.scrollTo(0, 0)
          sendMailSuccess.value = true
          setTimeout(() => {
            sendMailSuccess.value = false
            return navigateTo({ path: '/' })
          }, 2000)
        }
      } else {
        ElNotification({
          message: t('update_error')
        })
        return false
      }
    })
    submitPedding.value = false
  }
}

const handleResetPassword = async () => {
  if (!resetFormRef.value) return false
  // 避免double click
  if (submitPedding.value === false) {
    submitPedding.value = true
    await resetFormRef.value.validate(async (valid, invalidFields) => {
      if (valid) {
        const googleReCaptchaToken = await useReCaptchaV3(useReCaptchaInstance, 'forgot')

        const params = {
          token: token,
          email: email,
          password: resetForm.password,
        } as IResetPasswordParams

        const { data, pending, error } = await auth.resetPassword(params)
        if (error.value) {
          ElNotification({
            message: error.value.message,
          })
        } else {
          resetForm.password = ''
          resetForm.confirm_password = ''
          resetFormRef.value?.resetFields()
          window.scrollTo(0, 0)
          resetPasswordSuccess.value = true
          setTimeout(() => {
            resetPasswordSuccess.value = false
            return navigateTo({ path: '/member/login' })
          }, 2000)
        }
      } else {
        ElNotification({
          message: t('update_error')
        })
        return false
      }
    })
    submitPedding.value = false
  }
}

useSeo()
</script>

<template>
  <section class="memberPage">
    <div class="memberPage__inner">
      <div v-if="!sendMailSuccess && !resetPasswordSuccess">
        <TitleTitle3 class="memberPage__mainTitle" :title="isResetPwd === false ? $t('member.forget_password.forget_title') : $t('member.forget_password.reset_title')" />
        <div class="memberPage__form" v-if="isResetPwd === false">
          <el-form :model="form" ref="formRef">
            <div class="memberPage__form__item">
              <el-form-item prop="email" :rules="[
                {
                  required: true,
                  message: '電子信箱 is a required field'
                },
                {
                  type: 'email',
                  message: $t('email_validate'),
                },
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.forget_password.email')}}</FormTitle>
                <div class="relative w-full">
                  <el-input v-model="form.email" class="el-iw-input" />
                  <ButtonButton1 class="checkMailButton" @click="handleCheckMail" :title="$t('member.forget_password.send')"
                    :disabled="submitPedding" />
                </div>
              </el-form-item>
            </div>
          </el-form>
        </div>

        <div class="memberPage__form" v-if="isResetPwd">
          <el-form :model="resetForm" ref="resetFormRef">
            <div class="memberPage__form__item">
              <el-form-item prop="password" :rules="[
                {
                  required: true,
                  message: '密碼 is a required field'
                }
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.forget_password.password')}}</FormTitle>
                <el-input v-model="resetForm.password" type="password" :placeholder="$t('password_validate')"
                  class="el-iw-input" />
              </el-form-item>
            </div>

            <div class="memberPage__form__item">
              <el-form-item prop="confirm_password" :rules="[
                {
                  required: true,
                  message: '確認密碼 is a required field'
                }
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.forget_password.check_password')}}</FormTitle>
                <el-input v-model="resetForm.confirm_password" type="password" class="el-iw-input" />
              </el-form-item>
            </div>

            <div class="memberPage__form__item button">
              <ButtonButton1 :title="$t('member.forget_password.submit')" @click="handleResetPassword" :disabled="submitPedding" />
            </div>
          </el-form>
        </div>
      </div>
      <div v-if="sendMailSuccess">
        <MessageBox>{{ $t('member.forget_password.success_send')}}</MessageBox>
      </div>
      <div v-if="resetPasswordSuccess">
        <MessageBox>{{ $t('member.forget_password.success_reset')}}</MessageBox>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.checkMailButton {
  position: absolute;
  right: 0;
  top: -30px;
  font-size: 13px;

  @include max-media(480) {
    font-size: 10px;
  }
}

.memberPage {
  &__form {
    &__item {
      &.button {
        display: flex;
        justify-content: center;
        padding-top: 60px;

        @include max-media(480) {
          padding-top: 40px;
        }
      }
    }
  }
}

:deep(.el-form-item.is-error .el-input__wrapper) {
  box-shadow: unset;
}
</style>
